<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue'
import type {
  Attachment,
  ExternalSubject,
  FineUploaderUpload,
} from '@/models/current-student-advanced-standing-form/api'
import type { AlertTone } from '@/models/current-student-advanced-standing-form/modalAlert'
import CrudModalShell from '@/components/current-student-advanced-standing-form/shared/CrudModalShell.vue'
import ExternalSubjectAttachments from '@/components/current-student-advanced-standing-form/modals/external-subject/ExternalSubjectAttachments.vue'
import ExternalSubjectDetailsFieldset from '@/components/current-student-advanced-standing-form/modals/external-subject/ExternalSubjectDetailsFieldset.vue'
import ModalAlert from '@/components/current-student-advanced-standing-form/shared/ModalAlert.vue'

defineProps<{
  shown: boolean
  active: boolean
  externalSubjectCode: string
  externalSubjectName: string
  externalSubjectYear: string
  externalSubjectLevel: string
  externalSubjectUrl: string
  currentExternalSubject: Partial<ExternalSubject> | null
  currentSubjectAttachments: Attachment[]
  subjectAttachmentDeleteIds: number[]
  showSubjectFineUploader: boolean
  visibleSubjectNewAttachments: FineUploaderUpload[]
  subjectUploadDropActive: boolean
  subjectUploadAlertMessage: string
  errorMessage: string
  alertTone: AlertTone
  saving: boolean
  formRef: (element: Element | ComponentPublicInstance | null) => void
  uploadThumbnailUrl: (attachment: FineUploaderUpload) => string
}>()

const emit = defineEmits<{
  'update:externalSubjectCode': [value: string]
  'update:externalSubjectName': [value: string]
  'update:externalSubjectYear': [value: string]
  'update:externalSubjectLevel': [value: string]
  'update:externalSubjectUrl': [value: string]
  subjectCodeBeforeInput: [event: Event]
  stageSubjectAttachmentDelete: [attachment: Attachment]
  subjectUploadDragEnter: [event: DragEvent]
  subjectUploadDragOver: [event: DragEvent]
  subjectUploadDragLeave: [event: DragEvent]
  subjectUploadDrop: [event: DragEvent]
  triggerUploadInput: [event: MouseEvent]
  uploadButtonKeydown: [event: KeyboardEvent]
  uploadButtonHover: [event: MouseEvent, hover: boolean]
  uploadButtonFocus: [event: FocusEvent, focused: boolean]
  subjectAttachmentSelected: [event: Event]
  cancelStagedUpload: [attachment: FineUploaderUpload]
  retrySubjectAttachment: [attachment: FineUploaderUpload]
  deleteStagedSubjectAttachment: [attachment: FineUploaderUpload]
  closeSubjectUploadAlert: []
  delete: []
  close: []
  save: []
}>()

function onExternalSubjectUrlInput(event: Event): void {
  emit('update:externalSubjectUrl', (event.target as HTMLInputElement).value)
}
</script>

<template>
  <CrudModalShell
    modal-id="ExternalSubjectModal"
    title="Add Subject"
    :shown="shown"
    :active="active"
    delete-button-id="ExternalSubjectModalDeleteBtn"
    :delete-hidden="currentExternalSubject?.external_subject_id == null"
    save-button-id="ExternalSubjectModalSaveBtn"
    :saving="saving"
    cancel-button-class="btn btn-secondary"
    @close="emit('close')"
    @delete="emit('delete')"
    @save="emit('save')"
  >
          <p>Please enter <b>one</b> subject.</p>
          <p>It is important that the details you enter match exactly what is on the transcript.</p>
          <form :ref="formRef" @submit.prevent="emit('save')">
            <ExternalSubjectDetailsFieldset
              :external-subject-code="externalSubjectCode"
              :external-subject-name="externalSubjectName"
              :external-subject-year="externalSubjectYear"
              :external-subject-level="externalSubjectLevel"
              @update:external-subject-code="emit('update:externalSubjectCode', $event)"
              @update:external-subject-name="emit('update:externalSubjectName', $event)"
              @update:external-subject-year="emit('update:externalSubjectYear', $event)"
              @update:external-subject-level="emit('update:externalSubjectLevel', $event)"
              @subject-code-before-input="emit('subjectCodeBeforeInput', $event)"
            />

            <fieldset>
              <legend>Subject Evidence Details</legend>
              <p>
                Please provide a subject learning guide (SLG), subject outline, or syllabus.
                The subject evidence MUST be from the year you studied the subject, and it should at least contain subject description, class requirements, intended learning outcomes and assessment tasks.
                You can either upload it here or provide a valid URL to the information below.
              </p>

              <div class="form-field">
                <label for="externalSubjectUrlInput">URL of the subject learning guide</label>
                <input
                  id="externalSubjectUrlInput"
                  :value="externalSubjectUrl"
                  class="form-control"
                  data-admissions-name="external_subject_url"
                  data-admissions-group="SubjectInfo"
                  @input="onExternalSubjectUrlInput"
                >
              </div>

              <ExternalSubjectAttachments
                :current-subject-attachments="currentSubjectAttachments"
                :subject-attachment-delete-ids="subjectAttachmentDeleteIds"
                :show-subject-fine-uploader="showSubjectFineUploader"
                :visible-subject-new-attachments="visibleSubjectNewAttachments"
                :subject-upload-drop-active="subjectUploadDropActive"
                :subject-upload-alert-message="subjectUploadAlertMessage"
                :upload-thumbnail-url="uploadThumbnailUrl"
                @stage-subject-attachment-delete="emit('stageSubjectAttachmentDelete', $event)"
                @subject-upload-drag-enter="emit('subjectUploadDragEnter', $event)"
                @subject-upload-drag-over="emit('subjectUploadDragOver', $event)"
                @subject-upload-drag-leave="emit('subjectUploadDragLeave', $event)"
                @subject-upload-drop="emit('subjectUploadDrop', $event)"
                @trigger-upload-input="emit('triggerUploadInput', $event)"
                @upload-button-keydown="emit('uploadButtonKeydown', $event)"
                @upload-button-hover="(event, hover) => emit('uploadButtonHover', event, hover)"
                @upload-button-focus="(event, focused) => emit('uploadButtonFocus', event, focused)"
                @subject-attachment-selected="emit('subjectAttachmentSelected', $event)"
                @cancel-staged-upload="emit('cancelStagedUpload', $event)"
                @retry-subject-attachment="emit('retrySubjectAttachment', $event)"
                @delete-staged-subject-attachment="emit('deleteStagedSubjectAttachment', $event)"
                @close-subject-upload-alert="emit('closeSubjectUploadAlert')"
              />
            </fieldset>
          </form>
          <ModalAlert alert-id="externalSubjectModalError" :alert-tone="alertTone" :message="errorMessage" />
  </CrudModalShell>
</template>
